import type { ClientEngine, ReplayConfiguration, SpinResponse } from "@slot/client-engine";
import type { BalanceStore } from "../balance/BalanceStore";
import type { BetSystem } from "../bet/BetSystemTypes";
import type { GambleOfferFlow } from "../gamble/GambleOfferFlow";
import { SpinCoordinator } from "../spin/SpinCoordinator";
import type { SpinFailurePresenter } from "../spin/SpinFailurePresenter";
import type { SpinMotionControl } from "../spin/SpinMotionControl";
import type { SpinRequestBuilder } from "../spin/SpinRequestBuilder";
import { replayConfigurationForSpeed } from "../spin/replayConfigurationForSpeed";
import type { BootFallbackGridSpec, BootGridPresenterPort } from "./ports/BootGridPresenterPort";
import type { Log, PresentationBarrier, PresentationClock, TimerSleep } from "@slot/presentation";

export interface GameRuntimeInput {
  engine: ClientEngine;
  bootGridPresenter: BootGridPresenterPort;
  bootFallbackGrid: BootFallbackGridSpec;
  requestBuilder: SpinRequestBuilder;
  motion: SpinMotionControl;
  failure: SpinFailurePresenter;
  /** Absent when the settings or the game have no gamble. */
  gamble: GambleOfferFlow | null;
  betSystem: BetSystem;
  balanceStore: BalanceStore;
  presentationBarrier: PresentationBarrier;
  presentationClock: PresentationClock;
  /** The presentation's `beforeRound` (decision 71), and the engine-time count-down that bounds it. */
  beforeRound?: () => Promise<void>;
  timer?: (seconds: number) => TimerSleep;
  log: Log;
}

/**
 * A game's rounds: the spin coordinator over the engine, with the request builder, the motion
 * control, the failure presenter and the gamble flow as its collaborators; the boot grid; the
 * settlement and restore entry points the boot flows and the presenters call.
 */
export class GameRuntime {
  private readonly engine: ClientEngine;
  private readonly bootGridPresenter: BootGridPresenterPort;
  private readonly bootFallbackGrid: BootFallbackGridSpec;
  private readonly balanceStore: BalanceStore;
  private readonly log: Log;
  private readonly spinCoordinator: SpinCoordinator;

  public constructor(input: GameRuntimeInput) {
    this.engine = input.engine;
    this.bootGridPresenter = input.bootGridPresenter;
    this.bootFallbackGrid = input.bootFallbackGrid;
    this.balanceStore = input.balanceStore;
    this.log = input.log;
    const { engine, requestBuilder, motion, failure, gamble } = input;
    this.spinCoordinator = new SpinCoordinator({
      requestPlay: (request, configuration) => engine.requestPlay(request, configuration),
      buildSpinRequest: (request) => requestBuilder.build(request),
      buildReplayConfiguration: replayConfigurationForSpeed,
      betSystem: input.betSystem,
      balanceStore: input.balanceStore,
      presentationBarrier: input.presentationBarrier,
      preSpin: () => motion.preSpin(),
      onSpeedModeChange: (mode) => motion.setSpeedMode(mode),
      onSlamStop: () => motion.slamStop(),
      playExistingRound: (spinResponse, configuration) => engine.playSpinResponse(spinResponse, configuration),
      onSpinRejected: (rejection) => failure.onRejected(rejection),
      onSpinError: (error) => failure.onError(error),
      shouldOfferGamble: gamble ? (roundSnapshot) => gamble.shouldOffer(roundSnapshot) : undefined,
      onGamblePhase: gamble ? (offer) => gamble.run(offer) : undefined,
      ...(input.beforeRound ? { beforeRound: input.beforeRound } : {}),
      ...(input.timer ? { timer: input.timer } : {}),
      log: input.log,
    });
    input.presentationClock.setMode(this.spinCoordinator.getSnapshot().speedMode);
  }

  public getSpinCoordinator(): SpinCoordinator {
    return this.spinCoordinator;
  }

  public async start(): Promise<void> {
    this.log("[GameRuntime] start");
    await this.bootGridPresenter.showInitialGrid(this.bootFallbackGrid);
  }

  public whenInitialGridReady(): Promise<void> {
    return this.bootGridPresenter.whenInitialGridReady();
  }

  public async buyFeature(input: { featureId: string }): Promise<void> {
    this.log(`[GameRuntime] buyFeature feature=${input.featureId}`);
    await this.spinCoordinator.requestFeatureSpin(input.featureId);
  }

  /** `buyFeature` as soon as the spin coordinator is idle (a panel may ask mid-round). */
  public buyFeatureWhenIdle(featureId: string): void {
    if (this.spinCoordinator.getSnapshot().phase === "idle") {
      void this.buyFeature({ featureId });
      return;
    }
    const unsubscribe = this.spinCoordinator.subscribe((snapshot) => {
      if (snapshot.phase !== "idle") return;
      unsubscribe();
      void this.buyFeature({ featureId });
    });
  }

  public async playReplayRound(spinResponse: SpinResponse, replayConfiguration?: Partial<ReplayConfiguration>): Promise<void> {
    this.log("[GameRuntime] playReplayRound start");
    await this.engine.playSpinResponse(spinResponse, replayConfiguration);
    this.log("[GameRuntime] playReplayRound complete");
  }

  /** Presents a restored round; the bet stays locked under boot's reason until the boot releases it after the settlement. */
  public async presentRestoredRound(spinResponse: SpinResponse): Promise<void> {
    this.log("[GameRuntime] presentRestoredRound start");
    await this.spinCoordinator.presentRestoredRound(spinResponse, { keepBetLocked: true });
    this.log("[GameRuntime] presentRestoredRound complete");
  }

  /**
   * Settles a restored round through the engine's per-round cache, the same door every other
   * settlement uses. A restored round replays through the same plugins as a live one, so a winning
   * feature round has already sent the feature-finished panel's early settlement by the time this
   * runs. Through the cache this joins that request, in flight or done; through the engine's
   * uncached door it used until 2026-09-23 (deleted since, decision 35) it sent a second one, and
   * two confirms for one round in parallel is the one case the backend answers with a 500 instead
   * of its idempotent 200 — "Restore Failed" for a round that settled fine (review finding 23). If
   * the early one FAILED, this is the one retry decision 17 allows.
   */
  public async settlePendingRound(roundIdentifier: string): Promise<void> {
    this.log(`[GameRuntime] settlePendingRound start roundId=${roundIdentifier}`);
    const response = await this.engine.acknowledgePresentationCompleteOnce({ roundIdentifier });
    const balance = response.accountBalanceSnapshot.balanceAmount;
    this.balanceStore.setBalanceMinor(balance);
    this.log(`[GameRuntime] settlePendingRound complete balance=${balance}`);
  }

  /**
   * Early settlement of the active round (the feature-finished panel settles as soon as it opens):
   * a head start, not the settlement that counts. The end-of-replay settlement goes through the same
   * per-round cache, so it joins this one while it is in flight or done. A failure here is logged,
   * not shown: the engine evicts it and the end-of-replay settlement sends once more, and only THAT
   * one's failure reaches the reload modal. Safe because the backend's presentation-complete is
   * idempotent per round (decision 17 as amended 2026-09-23).
   */
  public async acknowledgePresentationCompleteOnce(roundIdentifier: string): Promise<void> {
    this.log(`[GameRuntime] acknowledgePresentationCompleteOnce start roundId=${roundIdentifier}`);
    try {
      const response = await this.engine.acknowledgePresentationCompleteOnce({ roundIdentifier });
      const balance = response.accountBalanceSnapshot.balanceAmount;
      this.balanceStore.setBalanceMinor(balance);
      this.log(`[GameRuntime] acknowledgePresentationCompleteOnce complete balance=${balance}`);
    } catch (error) {
      this.log(`[GameRuntime] acknowledgePresentationCompleteOnce failed roundId=${roundIdentifier} message=${error instanceof Error ? error.message : String(error)}`);
    }
  }

  public async stop(): Promise<void> {
    this.log("[GameRuntime] stop");
  }
}
